import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.team_space.changed' }>
}

function changePhrase(change: string): string {
  switch (change) {
    case 'links_updated':
      return ' обновил(а) ссылки команды в '
    case 'card_created':
      return ' добавил(а) карточку в командном пространстве '
    case 'card_deleted':
      return ' удалил(а) карточку в командном пространстве '
    default:
      return ' обновил(а) командное пространство '
  }
}

export function ProjectTeamSpaceChangedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onProjectTeamSpaceClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload: any = (notification as any).payload ?? {}
  const project = payload.project ?? {}
  const actor = payload.actor ?? {}
  const change = typeof payload.change === 'string' ? payload.change : ''
  const projectTitle = typeof project.title === 'string' ? project.title : 'проект'
  const actorName = typeof actor.fullname === 'string' ? actor.fullname : 'Участник'
  const projectAvatarSrc = mediaSourceUrl(project.image_url) ?? undefined
  const projectSlug = getProjectSlug(project)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectTeamSpaceClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectTitle}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle="Командное пространство"
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
            sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
          >
            <Avatar
              src={mediaSourceUrl(actor.avatar_url) ?? undefined}
              alt={actorName}
              sx={{ width: 34, height: 34 }}
            />
          </Box>
          <Stack minWidth={0}>
            <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
              <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                {actorName}
              </ActorNameSpan>
              {changePhrase(change)}
              <ProjectNameQuoted
                name={projectTitle}
                pathKey={projectSlug}
                onNameClick={onProjectTeamSpaceClick}
              />
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} •{' '}
          {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
